import { usePromotionalStore } from '~/stores/promotional'
import { useSiteStore } from '~/stores/site'

export default defineNuxtPlugin({
  name: 'promotional-realtime',
  dependsOn: ['pinia'],
  setup(nuxtApp) {
    // Only run on client
    if (process.server) return

    // Wait for app to be fully mounted
    nuxtApp.hooks.hook('app:mounted', async () => {
      let promotionalStore, siteStore
      
      try {
        // Safely access stores after Pinia is initialized
        promotionalStore = usePromotionalStore()
        siteStore = useSiteStore()
      } catch (error) {
        console.error('[PromotionalRealtime] Error accessing stores:', error)
        return
      }

      // Cache route instance to avoid repeated useRoute() calls
      const route = useRoute()
      
      // Function to get dealership ID from current context
      const getDealershipId = () => {
        // Try to get from route params first (for CMS pages)
        if (route.params.id && typeof route.params.id === 'string') {
          return route.params.id
        }

        // Try to get from route path for CMS pages
        const path = route.path
        if (path.includes('/cms/')) {
          const cmsMatch = path.match(/\/cms\/([^\/]+)/)
          if (cmsMatch && cmsMatch[1]) {
            return cmsMatch[1]
          }
        }

        // Try to get from site store if it has a dealership ID property
        if (siteStore.isInitialized && (siteStore as any).dealershipId) {
          return (siteStore as any).dealershipId
        }

        // Fallback to hardcoded ID for testing
        return '966353f6-7170-4afd-9db3-3f1199eb0be8'
      }

      // Function to initialize subscription
      const initializeSubscription = async () => {
        try {
          const dealershipId = getDealershipId()
          
          if (!dealershipId) {
            return
          }
          
          // Load initial data from site store first (safer)
          try {
            if (siteStore.promotional && siteStore.promotional.length > 0) {
              promotionalStore.initializeFromData(siteStore.promotional)
            } else {
              promotionalStore.slides = []
              promotionalStore.lastUpdate = new Date()
            }
          } catch (dataError) {
            console.error('[PromotionalRealtime] Error loading initial data:', dataError)
          }
          
          // Then try to subscribe to realtime updates
          try {
            promotionalStore.subscribeToPromotionalSlides(dealershipId)
          } catch (subscriptionError) {
            console.error('[PromotionalRealtime] Subscription failed:', subscriptionError)
            // Don't throw - continue with data already loaded
          }
          
        } catch (error) {
          console.error('[PromotionalRealtime] Failed to initialize subscription:', error)
          
          // Try fallback data loading from site store
          try {
            if (siteStore.promotional && siteStore.promotional.length > 0) {
              promotionalStore.initializeFromData(siteStore.promotional)
            } else {
              promotionalStore.slides = []
              promotionalStore.lastUpdate = new Date()
            }
          } catch (fallbackError) {
            console.error('[PromotionalRealtime] Fallback data loading also failed:', fallbackError)
          }
        }
      }

      // Initialize immediately on client
      nextTick(() => {
        initializeSubscription()
      })

      // Also watch for site store initialization (secondary path)
      watch(() => siteStore.isInitialized, (initialized) => {
        if (initialized && !promotionalStore.realtimeSubscription) {
          initializeSubscription()
        }
      }, { immediate: true })

      // Fallback: Initialize after a delay if nothing worked
      setTimeout(() => {
        if (!promotionalStore.realtimeSubscription) {
          initializeSubscription()
        }
      }, 3000)

      // Watch for route changes to handle different dealerships
      const router = useRouter()
      router.beforeEach(async (to, from) => {
        // Only reinitialize if dealership ID changes
        const currentDealershipId = getDealershipId()
        const newDealershipId = to.params.id || currentDealershipId
        
        if (currentDealershipId !== newDealershipId) {
          promotionalStore.unsubscribe()
          await initializeSubscription()
        }
      })

      // Cleanup on page unload
      if (typeof window !== 'undefined') {
        window.addEventListener('beforeunload', () => {
          promotionalStore.unsubscribe()
        })
      }

      // Provide periodic health check (every 2 minutes)
      const healthCheckInterval = setInterval(() => {
        if (promotionalStore.isConnected) {
          // Connection is healthy, update score
          promotionalStore.updateConnectionHealth('success')
        } else if (!promotionalStore.isFallbackMode && !promotionalStore.isReconnecting) {
          // Not connected and not trying to reconnect, check if we should
          const dealershipId = getDealershipId()
          if (dealershipId && promotionalStore.canReconnect) {
            promotionalStore.autoReconnect(dealershipId)
          }
        }
      }, 120000) // 2 minutes

      // Cleanup health check interval on page unload
      if (typeof window !== 'undefined') {
        window.addEventListener('beforeunload', () => {
          if (healthCheckInterval) {
            clearInterval(healthCheckInterval)
          }
        })
      }

      // Provide enhanced connection status and controls for components
      nuxtApp.provide('promotionalRealtime', {
        // Connection status
        isConnected: computed(() => promotionalStore.isConnected),
        connectionStatus: computed(() => promotionalStore.connectionStatus),
        connectionHealth: computed(() => promotionalStore.connectionHealth),
        statusMessage: computed(() => promotionalStore.statusMessage),
        lastUpdate: computed(() => promotionalStore.lastUpdate),
        
        // Error recovery state
        isReconnecting: computed(() => promotionalStore.isReconnecting),
        isFallbackMode: computed(() => promotionalStore.isFallbackMode),
        reconnectAttempts: computed(() => promotionalStore.reconnectAttempts),
        maxReconnectAttempts: computed(() => promotionalStore.maxReconnectAttempts),
        connectionHealthScore: computed(() => promotionalStore.connectionHealthScore),
        
        // Manual controls
        reconnect: () => {
          const dealershipId = getDealershipId()
          if (dealershipId) {
            promotionalStore.reconnect(dealershipId)
          }
        },
        forceReconnect: () => {
          const dealershipId = getDealershipId()
          if (dealershipId) {
            promotionalStore.forceReconnect(dealershipId)
          }
        },
        enableFallbackMode: () => {
          promotionalStore.enableFallbackMode()
        },
        disableFallbackMode: () => {
          const dealershipId = getDealershipId()
          if (dealershipId) {
            promotionalStore.disableFallbackMode(dealershipId)
          }
        }
      })
    })
  }
})